import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Button } from '@mui/material';
import { ArrowBack, ArrowForward, ArrowForward as ArrowIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeContext';
import { heroSlides as defaultSlides, type HeroSlide } from '../config/heroSlides';

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '80vh',
  minHeight: '600px',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    height: '70vh',
    minHeight: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '60vh',
    minHeight: '400px',
  },
}));

const SlideImage = styled('img')<{ $slideId?: number }>(({ $slideId }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: $slideId === 2 ? 'center center' : $slideId === 4 ? 'center 60%' : 'center 15%',
  position: 'absolute',
  top: 0,
  left: 0,
}));

const SlideOverlay = styled(Box)<{ $isDark: boolean }>(({ $isDark }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: $isDark
    ? 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)'
    : 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.5) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const SlideContent = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  zIndex: 2,
  padding: theme.spacing(4),
  maxWidth: '800px',
  margin: '0 auto',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(139, 92, 246, 0.3)',
  backdropFilter: 'blur(10px)',
  color: 'white',
  border: '2px solid rgba(139, 92, 246, 0.6)',
  zIndex: 3,
  width: '56px',
  height: '56px',
  boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.5)',
    border: '2px solid rgba(139, 92, 246, 0.8)',
    transform: 'translateY(-50%) scale(1.1)',
    boxShadow: '0 6px 24px rgba(139, 92, 246, 0.4)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    width: '48px',
    height: '48px',
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  },
}));

const DotsContainer = styled(Box)({
  position: 'absolute',
  bottom: '30px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '12px',
  zIndex: 3,
});

const Dot = styled(Box)<{ $active: boolean }>(({ $active }) => ({
  width: $active ? '24px' : '8px',
  height: '8px',
  borderRadius: '4px',
  backgroundColor: $active ? '#8B5CF6' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: $active ? '#8B5CF6' : 'rgba(255, 255, 255, 0.8)',
  },
}));

const GlassButton = styled(Button)<{ $isDark: boolean }>(({ $isDark }) => ({
  background: 'rgba(139, 92, 246, 0.4)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '2px solid rgba(139, 92, 246, 0.7)',
  borderRadius: '16px',
  padding: '18px 36px',
  fontSize: '1.2rem',
  fontWeight: 700,
  color: 'white',
  textTransform: 'none',
  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
  boxShadow: '0 6px 24px rgba(139, 92, 246, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(139, 92, 246, 0.6)',
    border: '2px solid rgba(139, 92, 246, 0.9)',
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(139, 92, 246, 0.5)',
  },
}));

interface HeroCarouselProps {
  slides?: HeroSlide[];
  autoPlayInterval?: number;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ 
  slides = defaultSlides, 
  autoPlayInterval = 4000 
}) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [currentSlide, isAutoPlaying, autoPlayInterval, slides.length]);

  const handlePrevious = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleDotClick = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <CarouselContainer>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{ width: '100%', height: '100%' }}
        >
          <SlideImage 
            src={slides[currentSlide].imageUrl} 
            alt={slides[currentSlide].title}
            loading="eager"
            $slideId={slides[currentSlide].id}
          />
          <SlideOverlay $isDark={isDark}>
            {!slides[currentSlide].title ? (
              // Special positioning for slide 1 (no title)
              <Box sx={{
                position: 'absolute',
                left: '10%',
                top: '70%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                textAlign: 'left',
              }}>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 600,
                      mb: 4,
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].subtitle}
                  </Typography>
                  <Link to={slides[currentSlide].ctaLink} style={{ textDecoration: 'none' }}>
                    <GlassButton
                      $isDark={isDark}
                      endIcon={<ArrowIcon />}
                    >
                      {slides[currentSlide].ctaText}
                    </GlassButton>
                  </Link>
                </motion.div>
              </Box>
            ) : slides[currentSlide].id === 4 ? (
              // Special positioning for slide 4 (bottom right)
              <Box sx={{
                position: 'absolute',
                right: '10%',
                bottom: '15%',
                zIndex: 3,
                textAlign: 'right',
              }}>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                      lineHeight: 1.1,
                      color: 'white',
                      mb: 2,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].title}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 400,
                      mb: 4,
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].subtitle}
                  </Typography>
                  <Link to={slides[currentSlide].ctaLink} style={{ textDecoration: 'none' }}>
                    <GlassButton
                      $isDark={isDark}
                      endIcon={<ArrowIcon />}
                    >
                      {slides[currentSlide].ctaText}
                    </GlassButton>
                  </Link>
                </motion.div>
              </Box>
            ) : slides[currentSlide].id === 3 ? (
              // Special positioning for slide 3 (bottom left)
              <Box sx={{
                position: 'absolute',
                left: '10%',
                bottom: '15%',
                zIndex: 3,
                textAlign: 'left',
              }}>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                      lineHeight: 1.1,
                      color: 'white',
                      mb: 2,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].title}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 400,
                      mb: 4,
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].subtitle}
                  </Typography>
                  <Link to={slides[currentSlide].ctaLink} style={{ textDecoration: 'none' }}>
                    <GlassButton
                      $isDark={isDark}
                      endIcon={<ArrowIcon />}
                    >
                      {slides[currentSlide].ctaText}
                    </GlassButton>
                  </Link>
                </motion.div>
              </Box>
            ) : slides[currentSlide].id === 5 ? (
              // Special positioning for slide 5 (bottom right)
              <Box sx={{
                position: 'absolute',
                right: '10%',
                bottom: '15%',
                zIndex: 3,
                textAlign: 'right',
              }}>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                      lineHeight: 1.1,
                      color: 'white',
                      mb: 2,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].title}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 400,
                      mb: 4,
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].subtitle}
                  </Typography>
                  <Link to={slides[currentSlide].ctaLink} style={{ textDecoration: 'none' }}>
                    <GlassButton
                      $isDark={isDark}
                      endIcon={<ArrowIcon />}
                    >
                      {slides[currentSlide].ctaText}
                    </GlassButton>
                  </Link>
                </motion.div>
              </Box>
            ) : (
              // Normal centered positioning for other slides
              <SlideContent>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                {slides[currentSlide].title && (
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
                      lineHeight: 1.1,
                      color: 'white',
                      mb: 2,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    {slides[currentSlide].title}
                  </Typography>
                )}
                <Typography
                  variant={slides[currentSlide].title ? "h5" : "h3"}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: slides[currentSlide].title ? 400 : 600,
                    mb: 4,
                    fontSize: slides[currentSlide].title 
                      ? { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                      : { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                  }}
                >
                  {slides[currentSlide].subtitle}
                </Typography>
                <Link to={slides[currentSlide].ctaLink} style={{ textDecoration: 'none' }}>
                  <GlassButton
                    $isDark={isDark}
                    endIcon={<ArrowIcon />}
                  >
                    {slides[currentSlide].ctaText}
                  </GlassButton>
                </Link>
              </motion.div>
            </SlideContent>
            )}
          </SlideOverlay>
        </motion.div>
      </AnimatePresence>

      <NavigationButton
        onClick={handlePrevious}
        sx={{ left: 20 }}
        aria-label="Previous slide"
      >
        <ArrowBack />
      </NavigationButton>

      <NavigationButton
        onClick={handleNext}
        sx={{ right: 20 }}
        aria-label="Next slide"
      >
        <ArrowForward />
      </NavigationButton>

      <DotsContainer>
        {slides.map((_, index) => (
          <Dot
            key={index}
            $active={index === currentSlide}
            onClick={() => handleDotClick(index)}
            role="button"
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </DotsContainer>
    </CarouselContainer>
  );
};

export default HeroCarousel;